import { useEffect, useMemo, useRef, useState } from 'react';
import { format, isSameDay } from 'date-fns';
import clsx from 'clsx';

import { useDragGesture } from '../useDragGesture';
import { useNow } from '../useNow';
import { useResolvedTheme } from '../theme';
import type { Plan, PlanBlock, PlanEvent, Rhythm } from './types';
import { clockLabel, minuteOfDay, timeRange, tint } from './util';

const TIME_COL = 52;
const MIN_HOUR = 38;
const SNAP = 15;

interface Props {
  days: Date[];
  plan: Plan;
  rhythms: Map<string, Rhythm>;
  onOpenBlock: (block: PlanBlock, day: string) => void;
  onOpenEvent: (event: PlanEvent) => void;
  onOpenUnplaced: (day: string) => void;
  onMoveBlock: (block: PlanBlock, start: Date, end: Date) => void;
  onPickSlot: (start: Date) => void;
}

type Item =
  | { kind: 'event'; key: string; event: PlanEvent; s: number; e: number }
  | { kind: 'block'; key: string; block: PlanBlock; s: number; e: number };

type Laid = Item & { col: number; cols: number };

function layout(items: Item[]): Laid[] {
  const sorted = [...items].sort((a, b) => a.s - b.s || b.e - a.e);
  const out: Laid[] = [];
  let cluster: Item[] = [];
  let clusterEnd = -1;
  const flush = () => {
    const ends: number[] = [];
    const cols: number[] = [];
    for (const it of cluster) {
      let c = ends.findIndex((e) => e <= it.s);
      if (c === -1) {
        c = ends.length;
        ends.push(it.e);
      } else ends[c] = it.e;
      cols.push(c);
    }
    cluster.forEach((it, i) => out.push({ ...it, col: cols[i], cols: ends.length }));
    cluster = [];
  };
  for (const it of sorted) {
    if (cluster.length && it.s >= clusterEnd) flush();
    cluster.push(it);
    clusterEnd = Math.max(clusterEnd, it.e);
  }
  if (cluster.length) flush();
  return out;
}

export default function PlanWeek({
  days,
  plan,
  rhythms,
  onOpenBlock,
  onOpenEvent,
  onOpenUnplaced,
  onMoveBlock,
  onPickSlot
}: Props) {
  const theme = useResolvedTheme();
  const now = useNow();
  const scrollRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const [hourH, setHourH] = useState(MIN_HOUR);

  const wakeMin = Math.min(...plan.days.map((d) => minuteOfDay(new Date(d.wake))));
  const h0 = Math.max(0, Math.floor(wakeMin / 60) - 1);
  const h1 = 24;
  const hours = h1 - h0;

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const measure = () => setHourH(Math.max(MIN_HOUR, el.clientHeight / hours));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [hours]);

  const byDay = useMemo(() => {
    const m = new Map<string, { timed: Item[]; allDay: PlanEvent[] }>();
    for (const d of days) m.set(format(d, 'yyyy-MM-dd'), { timed: [], allDay: [] });
    for (const ev of plan.events) {
      if (ev.all_day) {
        // All-day events are stored at UTC midnight.
        const key = ev.start.slice(0, 10);
        m.get(key)?.allDay.push(ev);
        continue;
      }
      const s = new Date(ev.start);
      const e = new Date(ev.end);
      const key = format(s, 'yyyy-MM-dd');
      const bucket = m.get(key);
      if (!bucket) continue;
      const sm = minuteOfDay(s);
      const em = isSameDay(s, e) ? minuteOfDay(e) : 24 * 60;
      if (em <= sm) continue;
      bucket.timed.push({ kind: 'event', key: `e:${ev.id}`, event: ev, s: sm, e: em });
    }
    for (const day of plan.days) {
      const bucket = m.get(day.date);
      if (!bucket) continue;
      for (const b of day.blocks) {
        const s = new Date(b.start);
        const e = new Date(b.end);
        const sm = minuteOfDay(s);
        const em = isSameDay(s, e) ? minuteOfDay(e) : 24 * 60;
        bucket.timed.push({ kind: 'block', key: `b:${b.id}`, block: b, s: sm, e: Math.max(em, sm + 15) });
      }
    }
    return m;
  }, [days, plan]);

  const allDayRows = Math.max(0, ...[...byDay.values()].map((b) => b.allDay.length));

  // Dragging a proposal or pin moves it in 15-minute steps and across days.
  const [drag, setDrag] = useState<{ id: string; dDays: number; dMin: number } | null>(null);
  const { startDrag, consumeDragClick } = useDragGesture();
  const colW = () => {
    const el = gridRef.current;
    return el ? (el.clientWidth - TIME_COL) / 7 : 1;
  };
  const snap = (px: number) => Math.round(((px / hourH) * 60) / SNAP) * SNAP;

  function beginMove(block: PlanBlock, ev: React.MouseEvent) {
    if (block.type === 'done') return;
    const x0 = ev.clientX;
    const y0 = ev.clientY;
    startDrag(ev, {
      cursor: 'grabbing',
      onMove: (m) => {
        setDrag({ id: block.id, dDays: Math.round((m.clientX - x0) / colW()), dMin: snap(m.clientY - y0) });
      },
      onEnd: (m) => {
        const dDays = Math.round((m.clientX - x0) / colW());
        const dMin = snap(m.clientY - y0);
        setDrag(null);
        if (!dDays && !dMin) return;
        const s = new Date(block.start);
        const e = new Date(block.end);
        s.setDate(s.getDate() + dDays);
        e.setDate(e.getDate() + dDays);
        s.setMinutes(s.getMinutes() + dMin);
        e.setMinutes(e.getMinutes() + dMin);
        onMoveBlock(block, s, e);
      },
      onCancel: () => setDrag(null)
    });
  }

  const nowMin = minuteOfDay(now);
  const top = (min: number) => ((min - h0 * 60) / 60) * hourH;

  return (
    <div ref={gridRef} className="flex min-h-0 min-w-0 flex-1 flex-col">
      {/* Day headers */}
      <div
        className="grid flex-shrink-0 border-b border-[var(--border)]"
        style={{ gridTemplateColumns: `${TIME_COL}px repeat(7, minmax(0, 1fr))` }}
      >
        <div />
        {days.map((d) => {
          const key = format(d, 'yyyy-MM-dd');
          const isToday = key === plan.today;
          const pd = plan.days.find((x) => x.date === key);
          const unplaced = pd?.unplaced.length ?? 0;
          const past = key < plan.today;
          return (
            <div key={key} className="flex items-center justify-between gap-1 border-l border-[var(--border)] px-2 py-2">
              <div className={clsx('flex items-baseline gap-[6px]', past && 'opacity-60')}>
                <span className={clsx('text-[0.7rem] uppercase tracking-wide', isToday ? 'text-[var(--accent)]' : 'text-[var(--text-faint)]')}>
                  {format(d, 'EEE')}
                </span>
                <span
                  className={clsx(
                    'grid h-[26px] min-w-[26px] place-items-center rounded-full px-1 text-[0.98rem] font-semibold',
                    isToday && 'bg-[var(--accent)] text-[var(--accent-fg)]'
                  )}
                >
                  {format(d, 'd')}
                </span>
              </div>
              {unplaced > 0 && (
                <button
                  type="button"
                  onClick={() => onOpenUnplaced(key)}
                  title="Sessions with no room this day"
                  className="rounded-full border border-dashed border-[var(--border-strong)] px-2 py-[1px] text-[0.7rem] text-[var(--text-muted)] hover:border-[var(--text-muted)] hover:text-[var(--text)]"
                >
                  +{unplaced} no room
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* All-day events */}
      {allDayRows > 0 && (
        <div
          className="grid flex-shrink-0 border-b border-[var(--border)]"
          style={{ gridTemplateColumns: `${TIME_COL}px repeat(7, minmax(0, 1fr))` }}
        >
          <div />
          {days.map((d) => {
            const key = format(d, 'yyyy-MM-dd');
            return (
              <div key={key} className="flex min-w-0 flex-col gap-[2px] border-l border-[var(--border)] p-1">
                {byDay.get(key)?.allDay.map((ev) => {
                  const t = tint(ev.color, theme);
                  return (
                    <button
                      key={ev.id}
                      type="button"
                      onClick={() => onOpenEvent(ev)}
                      className="truncate rounded px-[6px] py-[1px] text-left text-[0.72rem]"
                      style={{ background: t.softer, color: 'var(--text-muted)' }}
                    >
                      {ev.summary}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {/* Hours */}
      <div ref={scrollRef} className="relative min-h-0 flex-1 overflow-y-auto">
        <div
          className="relative grid"
          style={{ gridTemplateColumns: `${TIME_COL}px repeat(7, minmax(0, 1fr))`, height: hours * hourH }}
        >
          <div className="relative">
            {Array.from({ length: hours }, (_, i) => (
              <div
                key={i}
                className="absolute right-2 -translate-y-1/2 text-[0.68rem] text-[var(--text-faint)]"
                style={{ top: i * hourH }}
              >
                {i === 0 ? '' : clockLabel(new Date(2000, 0, 1, h0 + i))}
              </div>
            ))}
          </div>
          {days.map((d) => {
            const key = format(d, 'yyyy-MM-dd');
            const pd = plan.days.find((x) => x.date === key);
            const isToday = key === plan.today;
            const items = layout(byDay.get(key)?.timed ?? []);
            const wake = pd ? minuteOfDay(new Date(pd.wake)) : 7 * 60;
            const bedD = pd ? new Date(pd.bed) : null;
            const bed = bedD && format(bedD, 'yyyy-MM-dd') === key ? minuteOfDay(bedD) : 24 * 60;
            return (
              <div
                key={key}
                className={clsx('relative border-l border-[var(--border)]', key < plan.today && 'bg-[var(--bg-sunken)]')}
                onClick={(e) => {
                  if (e.target !== e.currentTarget) return;
                  const r = e.currentTarget.getBoundingClientRect();
                  const min = h0 * 60 + Math.floor(((e.clientY - r.top) / hourH) * 60 / SNAP) * SNAP;
                  const s = new Date(d);
                  s.setHours(0, min, 0, 0);
                  onPickSlot(s);
                }}
              >
                {Array.from({ length: hours }, (_, i) => (
                  <div
                    key={i}
                    className="pointer-events-none absolute inset-x-0 border-t border-[var(--border)]"
                    style={{ top: i * hourH, opacity: i === 0 ? 0 : 0.7 }}
                  />
                ))}
                {wake > h0 * 60 && (
                  <div className="plan-hatch pointer-events-none absolute inset-x-0 top-0" style={{ height: top(wake) }} />
                )}
                {bed < 24 * 60 && (
                  <div className="plan-hatch pointer-events-none absolute inset-x-0 bottom-0" style={{ top: top(bed) }} />
                )}

                {items.map((it) => {
                  const w = 100 / it.cols;
                  const style: React.CSSProperties = {
                    top: top(it.s) + 1,
                    height: Math.max(18, top(it.e) - top(it.s) - 2),
                    left: `calc(${it.col * w}% + 2px)`,
                    width: `calc(${w}% - 4px)`
                  };
                  if (it.kind === 'event') {
                    return (
                      <EventTile
                        key={it.key}
                        event={it.event}
                        rhythm={it.event.rhythm_id ? rhythms.get(it.event.rhythm_id) : undefined}
                        style={style}
                        onOpen={() => onOpenEvent(it.event)}
                      />
                    );
                  }
                  const r = rhythms.get(it.block.rhythm_id);
                  const dragging = drag?.id === it.block.id;
                  if (dragging) {
                    style.transform = `translate(${drag.dDays * colW()}px, ${(drag.dMin / 60) * hourH}px)`;
                    style.zIndex = 30;
                  }
                  return (
                    <BlockTile
                      key={it.key}
                      block={it.block}
                      rhythm={r}
                      style={style}
                      dragging={dragging}
                      dragMinutes={dragging ? drag.dMin : 0}
                      onMouseDown={(ev) => beginMove(it.block, ev)}
                      onOpen={() => {
                        if (consumeDragClick()) return;
                        onOpenBlock(it.block, key);
                      }}
                    />
                  );
                })}

                {isToday && nowMin >= h0 * 60 && (
                  <div className="pointer-events-none absolute inset-x-0 z-20" style={{ top: top(nowMin) }}>
                    <div className="relative h-[2px] bg-[var(--today-fg)]">
                      <span className="absolute -left-[4px] -top-[3px] h-[8px] w-[8px] rounded-full bg-[var(--today-fg)]" />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function EventTile({
  event,
  rhythm,
  style,
  onOpen
}: {
  event: PlanEvent;
  rhythm?: Rhythm;
  style: React.CSSProperties;
  onOpen: () => void;
}) {
  const theme = useResolvedTheme();
  const t = tint(event.color, theme);
  const rt = rhythm ? tint(rhythm.color, theme) : null;
  const s = new Date(event.start);
  const e = new Date(event.end);
  const tall = (style.height as number) > 40;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="absolute z-10 flex flex-col overflow-hidden rounded-md px-[6px] py-[3px] text-left"
      style={{
        ...style,
        background: t.solid,
        color: t.fg,
        boxShadow: rt ? `inset 0 0 0 2px ${rt.solid}` : undefined
      }}
      title={rt ? `Counts toward ${rhythm!.name}` : undefined}
    >
      <span className="flex min-w-0 items-center gap-1 text-[0.74rem] font-medium leading-tight">
        {rt && (
          <svg viewBox="0 0 12 12" width="10" height="10" className="flex-shrink-0" aria-hidden>
            <path d="M2.5 6.2 5 8.5l4.5-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
        <span className="truncate">{event.summary}</span>
      </span>
      {tall && <span className="truncate text-[0.68rem] opacity-80">{timeRange(s, e)}</span>}
    </button>
  );
}

function BlockTile({
  block,
  rhythm,
  style,
  dragging,
  dragMinutes,
  onMouseDown,
  onOpen
}: {
  block: PlanBlock;
  rhythm?: Rhythm;
  style: React.CSSProperties;
  dragging: boolean;
  dragMinutes: number;
  onMouseDown: (e: React.MouseEvent) => void;
  onOpen: () => void;
}) {
  const theme = useResolvedTheme();
  const t = tint(rhythm?.color, theme);
  const s = new Date(block.start);
  const e = new Date(block.end);
  if (dragMinutes) {
    s.setMinutes(s.getMinutes() + dragMinutes);
    e.setMinutes(e.getMinutes() + dragMinutes);
  }
  const tall = (style.height as number) > 40;
  let look: React.CSSProperties;
  switch (block.type) {
    case 'done':
      look = { background: t.solid, color: t.fg, border: `1px solid ${t.solid}` };
      break;
    case 'pin':
      look = { background: t.soft, border: `1.5px solid ${t.line}` };
      break;
    case 'missed':
      look = { background: 'var(--bg-muted)', border: `1.5px dashed var(--border-strong)`, color: 'var(--text-muted)' };
      break;
    default:
      look = { background: t.softer, border: `1.5px dashed ${t.line}` };
  }
  return (
    <button
      type="button"
      onMouseDown={onMouseDown}
      onClick={onOpen}
      className={clsx(
        'plan-block-in group absolute z-10 flex flex-col overflow-hidden rounded-md px-[6px] py-[3px] text-left transition-shadow',
        block.type !== 'done' && 'cursor-grab',
        dragging ? 'shadow-[var(--shadow-md)]' : 'hover:shadow-[var(--shadow-sm)]',
        block.type === 'missed' && 'plan-hatch'
      )}
      style={{ ...style, ...look }}
    >
      <span className="flex min-w-0 items-center gap-1 text-[0.74rem] font-medium leading-tight">
        {block.type === 'done' && (
          <svg viewBox="0 0 12 12" width="10" height="10" className="flex-shrink-0" aria-hidden>
            <path d="M2.5 6.2 5 8.5l4.5-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
        {block.type === 'pin' && (
          <span className="h-[6px] w-[6px] flex-shrink-0 rounded-full" style={{ background: t.solid }} aria-hidden />
        )}
        <span className="truncate">{rhythm?.name ?? 'Rhythm'}</span>
      </span>
      {tall && (
        <span className={clsx('truncate text-[0.68rem]', block.type === 'done' ? 'opacity-80' : 'text-[var(--text-muted)]')}>
          {timeRange(s, e)}
          {block.overdue && block.type === 'proposal' ? '  catch-up' : ''}
          {block.type === 'missed' ? '  missed' : ''}
        </span>
      )}
    </button>
  );
}
